import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Check, Sparkles, ArrowLeft, EyeOff, Palette } from 'lucide-react';
import { useT } from '../studioI18n';
import { useProject } from '../ProjectContext';
import { studioApi, apiError, fileUrl } from '../studioApi';
import { Page, MotionButton, stagger, rise } from '../components/Motion';
import { WindowConfigurator } from '../components/WindowConfigurator';
import { ColorMultiSwatches } from '../components/SpecPickers';
import { DetectProgress } from '../../components/DetectProgress';

// Abas de fotos (miniaturas; check = entra na simulação) + configurador da foto ativa
const PhotoTabs = ({ photos, activeId, onActive, selected, onToggle }) => {
  const { t } = useT();
  return (
    <div className="flex gap-2 overflow-x-auto st-scroll pb-1" data-testid="per-window-photo-tabs">
      {photos.map((p, i) => {
        const on = selected.includes(p.id);
        return (
          <div key={p.id} className={`relative shrink-0 h-16 w-24 rounded-xl overflow-hidden border-2 transition-colors ${p.id === activeId ? 'border-teal-600 shadow-md' : 'border-transparent'} ${on ? '' : 'opacity-50'}`} data-testid={`per-window-photo-tab-${i}`}>
            <button type="button" onClick={() => onActive(p.id)} aria-pressed={p.id === activeId} className="absolute inset-0" data-testid={`per-window-photo-select-${i}`}><img src={fileUrl(p.id, 'thumb')} alt="" className="h-full w-full object-cover" /></button>
            <button type="button" onClick={() => onToggle(p.id)} aria-pressed={on} title={on ? t.includePhoto : t.excludedPhoto} className={`absolute top-1 right-1 h-6 w-6 rounded-full flex items-center justify-center text-white shadow ${on ? 'bg-teal-600' : 'bg-slate-700/80'}`} data-testid={`sim-photo-toggle-${i}`}>{on ? <Check className="h-3.5 w-3.5" /> : <EyeOff className="h-3 w-3" />}</button>
            {(p.windows || []).length > 0 && <span className="absolute bottom-1 left-1 rounded-full bg-slate-900/80 text-white text-[10px] font-bold px-1.5 pointer-events-none">{(p.windows || []).length}</span>}
          </div>
        );
      })}
    </div>
  );
};

export const DesignStep = () => {
  const { t } = useT();
  const { project, options, reload, running, patchPhoto } = useProject();
  const navigate = useNavigate();
  const [sel, setSel] = useState(project.photos.map((p) => p.id));
  const [activeId, setActiveId] = useState(project.photos[0]?.id);
  const [busy, setBusy] = useState(false);
  const [extColors, setExtColors] = useState(() => (options?.palette.exterior || []).map((c) => c.id));
  const [intColors, setIntColors] = useState(() => (options?.palette.interior || []).map((c) => c.id));
  const hasInterior = project.photos.some((p) => p.photo_kind === 'interior');
  const hasExterior = project.photos.some((p) => p.photo_kind !== 'interior');
  useEffect(() => { if (!project.photos.some((p) => p.id === activeId)) setActiveId(project.photos[0]?.id); }, [project.photos, activeId]);
  useEffect(() => { if (options) { setExtColors(options.palette.exterior.map((c) => c.id)); setIntColors(options.palette.interior.map((c) => c.id)); } }, [options]);
  if (!options) return null;

  const photo = project.photos.find((p) => p.id === activeId) || project.photos[0];
  const globals = { window_style: 'double_hung', grille: 'none', glass_type: 'clear', screen: 'none' };
  const selExt = project.photos.filter((p) => sel.includes(p.id) && p.photo_kind !== 'interior').length;
  const selInt = project.photos.filter((p) => sel.includes(p.id) && p.photo_kind === 'interior').length;
  const totalImages = selExt * extColors.length + selInt * intColors.length;
  const colorsOk = (!hasExterior || extColors.length > 0) && (!hasInterior || intColors.length > 0);
  const canGo = sel.length > 0 && colorsOk && !running && !busy;
  const generate = async () => {
    setBusy(true);
    try {
      await studioApi.post('/simulate', { photo_ids: sel, window_style: globals.window_style, grille: globals.grille, glass_type: globals.glass_type, screen: globals.screen, exterior_color_ids: hasExterior ? extColors : [], interior_color_ids: hasInterior ? intColors : [] });
      toast.success(t.simStarted); await reload(); navigate('/project/reveal');
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Page testId="design-step">
      <motion.div variants={stagger} initial="initial" animate="animate" className="space-y-6">
        <motion.div variants={rise} className="text-center max-w-2xl mx-auto">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900">{t.dsTitle}</h1>
          <p className="text-slate-600 mt-2">{t.dsSub}</p>
        </motion.div>

        <motion.section variants={rise} className="space-y-3" data-testid="per-window-section">
          <PhotoTabs photos={project.photos} activeId={photo?.id} onActive={setActiveId} selected={sel} onToggle={(id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))} />
          {photo && (photo.windows_status === 'detecting'
            ? <div className="st-glass rounded-[1.5rem] px-4 py-3 text-teal-900 max-w-md" data-testid="per-window-detecting"><DetectProgress startedAt={photo.windows_started_at || photo.created_at} etaS={project.detect_eta_s} label={t.detecting} remainingLabel={t.detectRemaining} almostLabel={t.detectAlmost} testId="design-detect-progress" /></div>
            : <WindowConfigurator key={photo.id} photo={photo} options={options} globals={globals} onSaved={patchPhoto} />)}
        </motion.section>

        <motion.section variants={rise} className="st-glass-strong rounded-[1.5rem] p-4 sm:p-5 space-y-4" data-testid="colors-section">
          <div className="flex items-start gap-3">
            <span className="h-10 w-10 shrink-0 rounded-2xl bg-teal-600 text-white flex items-center justify-center"><Palette className="h-5 w-5" /></span>
            <div>
              <h2 className="font-display font-bold text-slate-900 text-base md:text-lg">{t.colorsTitle}</h2>
              <p className="text-sm text-slate-600 mt-0.5">{t.colorsSub}</p>
            </div>
          </div>
          {hasExterior && <div><p className="text-xs font-bold text-slate-600 mb-1.5">{t.extColorsTitle}</p><ColorMultiSwatches colors={options.palette.exterior} values={extColors} onChange={setExtColors} testPrefix="sim-ext" /></div>}
          {hasInterior && <div><p className="text-xs font-bold text-slate-600 mb-1.5">{t.intColorsTitle}</p><ColorMultiSwatches colors={options.palette.interior} values={intColors} onChange={setIntColors} testPrefix="sim-int" /></div>}
        </motion.section>
        <div className="h-16" />
      </motion.div>

      <div className="fixed bottom-0 inset-x-0 z-30 p-3 sm:p-4 bg-white/80 backdrop-blur-xl border-t border-white/80" style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <button type="button" onClick={() => navigate('/project/photos')} className="inline-flex items-center gap-1 text-sm font-bold text-slate-600 hover:text-slate-900" data-testid="design-back-btn"><ArrowLeft className="h-4 w-4" /> {t.back}</button>
          <div className="flex items-center gap-3">
            {!busy && !sel.length && <span className="hidden sm:inline text-xs text-slate-500">{t.pickPhotos2}</span>}
            {!busy && sel.length > 0 && !colorsOk && <span className="hidden sm:inline text-xs font-bold text-rose-600" data-testid="design-pick-colors-hint">{t.pickColors}</span>}
            {!busy && canGo && <span className="hidden sm:inline text-xs text-slate-500" data-testid="design-images-total">{t.imagesTotal(totalImages)}</span>}
            <MotionButton onClick={generate} disabled={!canGo} className="px-6" data-testid="generate-simulation-btn"><Sparkles className="h-5 w-5" /> {busy ? t.generating : t.generate}</MotionButton>
          </div>
        </div>
      </div>
    </Page>
  );
};
